import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, iif, of } from 'rxjs';
import { map, mergeMap, withLatestFrom, catchError } from 'rxjs/operators';

/* notification service */
import { NotificationService } from 'src/app/features/shared/services/notification-service/notification.service';

/* jsonata object class */
import { JSONAtaObject } from 'src/app/features/shared/helper-classes';

/* lodash lib */
import * as lodash from 'lodash';

/* DataStorage interface for ngrx */
import { DataStorage } from 'src/app/features/data-stores/data-storage.interface';

/* navigate action */
import { Navigate } from 'src/app/features/data-stores/router-data-store/state/router-data-store.actions';

/* Workflow Management API Service */
import { WorkflowManagementControllerService } from 'src/app/features/shared/sdk/services/workflow-management-controller.service';

/* transportation-data-store actions */
import * as TransportationDataStoreActions from 'src/app/features/data-stores/transportation-data-store/state/transportation-data-store.actions';

/* api services */
import { TransportationControllerService } from 'src/app/features/shared/sdk_v1/services';

export const navigatePathAfterCreatingInstance = '/transportation/';
export const navigatePathAfterUpdatingInstance = '/transportation/';
@Injectable()
export class TransportationDataStoreEffects {
  constructor(
    private actions$: Actions,
    private store: Store<DataStorage>,
    private notificationService: NotificationService,
    private workflowManagementApi: WorkflowManagementControllerService,
    private transportationApiService: TransportationControllerService,
  ) { 
    // prettier-ignore
    // #region customCode: data-store-effects__constructor__start
    
    // #endregion customCode: data-store-effects__constructor__start

    // prettier-ignore
    // #region customCode: data-store-effects__constructor__end
    
    // #endregion customCode: data-store-effects__constructor__end
  }

  GetActiveTransportationId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        TransportationDataStoreActions.InitFetchingActiveTransportationWithRelations,
      ),
      withLatestFrom(
        this.store.select((state) => state.RouterDataStore.params),
      ),
      /* dataset */
      mergeMap(([action, params]) => {
        const data = {
          eventPayload: action['payload'] ?? {},
          params,
        };
        const initialPayload = of(JSONAtaObject.chain(data));
        return initialPayload;
      }),

      // prettier-ignore
      // #region customCode: data-store-effects__pre-data-transformation__start

      // #endregion customCode: data-store-effects__pre-data-transformation__start

      /* pre data transformation */
      mergeMap(payloadAsJsonataObj => {
            /* Pre Data Transformation Step 1 ( Map ) */
            /* get value of the passed jsonata object */
            const data = <any>payloadAsJsonataObj.value;
            /* transform data */
            const transformedData = JSONAtaObject.chain(data).transform(`
              { "id": params.id }
            `);
            return transformedData;
          }),
      // prettier-ignore
      // #region customCode: data-store-effects__pre-data-transformation__end

      // #endregion customCode: data-store-effects__pre-data-transformation__end
      /* trigger success actions */
      mergeMap((jsonataObj) => {
            /* get value of the passed jsonata object */
            const data = <any>jsonataObj.value;
            return [
              TransportationDataStoreActions.GetActiveTransportationWithRelationsById({ payload: data }),
              ];
          }),
    ),
  );

  getTransportation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransportationDataStoreActions.GetTransportation),
      mergeMap((action) => {
        return this.transportationApiService.find().pipe(
          mergeMap((transportation) => {
            const payload: any = {
              transportation: transportation,
            };
            return [
              TransportationDataStoreActions.GetTransportationSuccessful({
                payload,
              }),
            ];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while fetching data.',
            );
            return [TransportationDataStoreActions.GetTransportationFailure()];
          }),
        );
      }),
    ),
  );

  getTransportationById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransportationDataStoreActions.GetTransportationById),
      mergeMap((action) => {
        return this.transportationApiService
          .findById({
            id: action.payload.id,
          })
          .pipe(
            mergeMap((transportation) => {
              const payload: any = {
                transportation: transportation,
              };
              return [
                TransportationDataStoreActions.GetTransportationByIdSuccessful({
                  payload,
                }),
              ];
            }),
            catchError((error) => {
              this.notificationService.createNotification(
                'error',
                'Failure',
                'There was an error while fetching data.',
              );
              return [
                TransportationDataStoreActions.GetTransportationByIdFailure(),
              ];
            }),
          );
      }),
    ),
  );

  getActiveTransportationWithRelationsById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        TransportationDataStoreActions.GetActiveTransportationWithRelationsById,
      ),
      mergeMap((action) => {
        return this.transportationApiService
          .findById({
            id: action.payload.id,
          })
          .pipe(
            mergeMap((transportation) => {
              const payload: any = {
                activeTransportation: transportation,
              };
              return [
                TransportationDataStoreActions.GetActiveTransportationByIdWithRelationsSuccessful(
                  { payload },
                ),
              ];
            }),
            catchError((error) => {
              this.notificationService.createNotification(
                'error',
                'Failure',
                'There was an error while fetching data.',
              );
              return [
                TransportationDataStoreActions.GetActiveTransportationByIdWithRelationsFailure(),
              ];
            }),
          );
      }),
    ),
  );

  createTransportation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransportationDataStoreActions.CreateTransportation),
      mergeMap((action) => {
        const params = {
          body: action.payload.transportation,
        };
        return this.transportationApiService.create(<any>params).pipe(
          mergeMap((transportation) => {
            this.notificationService.createNotification(
              'success',
              'Successful',
              'Your data has been saved successfully.',
            );
            const payload: any = {
              transportation: transportation,
            };
            return [
              TransportationDataStoreActions.CreateTransportationSuccessful({
                payload,
              }),
              Navigate({
                payload: { path: navigatePathAfterCreatingInstance },
              }),
            ];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while saving the data.',
            );
            return [
              TransportationDataStoreActions.CreateTransportationFailure(),
            ];
          }),
        );
      }),
    ),
  );

  updateTransportation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransportationDataStoreActions.UpdateTransportation),
      mergeMap((action) => {
        const params = {
          id: action.payload.id,
          body: action.payload.data,
        };
        return this.transportationApiService.updateById(<any>params).pipe(
          mergeMap(() => {
            this.notificationService.createNotification(
              'success',
              'Veri güncellendi',
              'Verileriniz başarıyla güncellendi.',
            );
            return [
              TransportationDataStoreActions.GetTransportation(),
              Navigate({
                payload: { path: navigatePathAfterUpdatingInstance },
              }),
            ];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while updating the data.',
            );
            return [
              TransportationDataStoreActions.UpdateTransportationFailure(),
            ];
          }),
        );
      }),
    ),
  );

  deleteTransportation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransportationDataStoreActions.DeleteTransportation),
      mergeMap((action) => {
        const params = { id: action.payload.id };
        return this.transportationApiService.deleteById(params).pipe(
          mergeMap(() => {
            this.notificationService.createNotification(
              'success',
              'Success',
              'Your data has been deleted successfully.',
            );
            const payload: any = {
              id: action.payload.id,
            };
            return [
              TransportationDataStoreActions.DeleteTransportationSuccessful({
                payload,
              }),
            ];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while deleting the data.',
            );
            return [
              TransportationDataStoreActions.DeleteTransportationFailure(),
            ];
          }),
        );
      }),
    ),
  );
}
